import { ANCESTORS, LINEAGE_PLACES } from '../data/ancestors';

const SELF = '#f4efe0';

const PLACE_COLOR = Object.fromEntries(LINEAGE_PLACES.map((place) => [place.id, place.color]));

const BY_ID = new Map(ANCESTORS.map((person) => [person.id, person]));
const DRAW_ORDER = ANCESTORS.filter((person) => person.gen > 0).sort((a, b) => b.gen - a.gen);
const LINKED = ANCESTORS.filter((person) => person.childId);
const BY_GEN = new Map();
const BY_PLACE = new Map();
ANCESTORS.forEach((person) => {
  if (person.gen <= 0) return;
  let list = BY_GEN.get(person.gen);
  if (!list) {
    list = [];
    BY_GEN.set(person.gen, list);
  }
  list.push(person);
  const place = person.birthplace || 'unknown';
  let placeSet = BY_PLACE.get(place);
  if (!placeSet) {
    placeSet = new Set();
    BY_PLACE.set(place, placeSet);
  }
  placeSet.add(person.id);
});

export function birthplaceLit(placeId) {
  if (!placeId) return null;
  return BY_PLACE.get(placeId) || null;
}

function placeColor(person) {
  if (!person || person.gen === 0) return SELF;
  return PLACE_COLOR[person.birthplace] || PLACE_COLOR.unknown || '#6e756f';
}

const ALPHA_CACHE = new Map();

function withAlpha(hex, alpha) {
  const key = `${hex}|${alpha}`;
  const cached = ALPHA_CACHE.get(key);
  if (cached) return cached;
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const value = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  ALPHA_CACHE.set(key, value);
  return value;
}

export function chainToRoot(id) {
  const ids = new Set();
  let current = BY_ID.get(id);
  while (current) {
    ids.add(current.id);
    current = current.childId ? BY_ID.get(current.childId) : null;
  }
  return ids;
}

function fanLayout(w, h, phone) {
  if (!phone) {
    const headerClear = 158;
    const footerClear = 84;
    const sidePad = 28;
    const legendReserve = 176;
    const labelPad = 36;
    const cx = w / 2;
    const availTop = headerClear;
    const availBottom = h - footerClear;
    const availH = Math.max(140, availBottom - availTop);
    const maxRByWidth = Math.min(cx - legendReserve - labelPad, w - sidePad - labelPad - cx);
    const maxRByHeight = (availH / 2) / 0.62;
    const maxR = Math.max(96, Math.min(maxRByWidth, maxRByHeight));
    const contentMid = availTop + availH / 2;
    let cy = contentMid + maxR * 0.42;
    if (cy - maxR < availTop) cy = availTop + maxR;
    if (cy + 28 > availBottom) cy = availBottom - 28;
    return { cx, cy, maxR, yScale: 1 };
  }

  // Same round fan as desktop, larger than the phone, with Kai parked in view.
  const headerClear = 92;
  const footerClear = 148;
  const cy = h - footerClear - 28;
  const visibleAbove = Math.max(200, cy - headerClear);
  const maxR = visibleAbove * 1.35;
  return { cx: w / 2, cy, maxR, yScale: 1 };
}

/** Every generation sits the same distance out from the one inside it. */
function generationRingR(gen, minR, maxR, maxGen) {
  const span = Math.max(1, maxGen - 1);
  const reach = Math.max(0, maxR - minR);
  return minR + ((Math.max(gen, 1) - 1) / span) * reach;
}

export function layoutLineage(w, h) {
  const phone = w < 768;
  const { cx, cy, maxR, yScale } = fanLayout(w, h, phone);
  const narrow = false;
  const minR = maxR * 0.2;
  const maxGen = ANCESTORS.reduce((max, person) => Math.max(max, person.gen || 0), 1);
  const arcStart = Math.PI * 1.08;
  const arcSweep = Math.PI * 0.84;
  const positions = new Map();

  ANCESTORS.forEach((person) => {
    if (person.gen === 0) {
      positions.set(person.id, {
        x: cx,
        y: cy,
        r: Math.max(narrow ? 18 : 22, maxR * 0.048),
        ringR: 0,
        gen: 0,
      });
      return;
    }
    const count = 2 ** person.gen;
    const t = (person.slot + 0.5) / count;
    const angle = arcStart + t * arcSweep;
    const ringR = generationRingR(person.gen, minR, maxR, maxGen);
    const spacing = (ringR * arcSweep) / count;
    let r = Math.max(narrow ? 4.2 : 2.8, (8 - person.gen) * maxR * 0.0055);
    if (person.photo) {
      const wanted = (narrow ? 8 : 12) + (6 - person.gen) * (narrow ? 0.7 : 1.4);
      r = Math.min(Math.max(r, wanted), Math.max(r, spacing * 0.42));
    }
    if (count >= 256) r = Math.min(r, Math.max(0.9, spacing * 0.32));
    else if (count >= 128) r = Math.min(r, Math.max(1.6, spacing * 0.36));
    positions.set(person.id, {
      x: cx + Math.cos(angle) * ringR,
      y: cy + Math.sin(angle) * ringR * yScale,
      r,
      ringR,
      angle,
      gen: person.gen,
    });
  });

  return { cx, cy, minR, maxR, yScale, maxGen, arcStart, arcSweep, positions, narrow, w, h };
}

const ancestorPhotos = new Map();
const spriteCache = new Map();
let lineagePhotoEpochValue = 0;

export function lineagePhotoEpoch() {
  return lineagePhotoEpochValue;
}

function ancestorPhoto(src) {
  if (!src) return null;
  let img = ancestorPhotos.get(src);
  if (!img) {
    img = new Image();
    img.decoding = 'async';
    img.src = src;
    img.addEventListener('load', () => {
      lineagePhotoEpochValue += 1;
    }, { once: true });
    ancestorPhotos.set(src, img);
  }
  if (!img.complete || img.naturalWidth <= 0) return null;
  return img;
}

/** Tint once onto a small sprite so the fan can blit it instead of clipping and filtering every frame. */
function tintedSprite(img, living) {
  const key = `${living ? 'L' : 'D'}|${img.src}`;
  const cached = spriteCache.get(key);
  if (cached) return cached;
  const size = 96;
  const sprite = document.createElement('canvas');
  sprite.width = size;
  sprite.height = size;
  const sctx = sprite.getContext('2d');
  const radius = size / 2;
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  sctx.beginPath();
  sctx.arc(radius, radius, radius, 0, Math.PI * 2);
  sctx.clip();
  sctx.fillStyle = living ? 'rgba(8, 18, 12, 0.96)' : 'rgba(12, 12, 12, 0.96)';
  sctx.fillRect(0, 0, size, size);
  if (iw && ih) {
    const scale = Math.max(size / iw, size / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    sctx.globalAlpha = 0.72;
    if (!living) sctx.filter = 'grayscale(1)';
    sctx.drawImage(img, radius - dw / 2, radius - dh / 2, dw, dh);
    sctx.filter = 'none';
    sctx.globalAlpha = 1;
    sctx.globalCompositeOperation = 'multiply';
    sctx.fillStyle = living ? 'rgb(110, 195, 145)' : 'rgb(176, 176, 176)';
    sctx.fillRect(0, 0, size, size);
    sctx.globalCompositeOperation = 'source-over';
    sctx.fillStyle = living ? 'rgba(40, 110, 72, 0.28)' : 'rgba(0, 0, 0, 0.28)';
    sctx.fillRect(0, 0, size, size);
  }
  spriteCache.set(key, sprite);
  return sprite;
}

function traceFan(ctx, ox, oy, radius, yScale, start, sweep, closeToCenter) {
  const steps = Math.max(28, Math.round(42 * (sweep / Math.PI)));
  ctx.beginPath();
  if (closeToCenter) ctx.moveTo(ox, oy);
  for (let i = 0; i <= steps; i += 1) {
    const angle = start + (sweep * i) / steps;
    const x = ox + Math.cos(angle) * radius;
    const y = oy + Math.sin(angle) * radius * yScale;
    if (!closeToCenter && i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  if (closeToCenter) ctx.closePath();
}

function hitTest(mx, my, positions, slop) {
  let best = null;
  positions.forEach((pos, id) => {
    const d = Math.hypot(mx - pos.x, my - pos.y);
    const reach = Math.max(pos.r + 8, slop);
    if (d <= reach && (!best || d < best.d)) best = { id, d };
  });
  return best ? best.id : null;
}

function surnameOf(person) {
  const parts = person.name.split(' ');
  return (parts[parts.length - 1] || person.name).toUpperCase();
}

function initialsOf(name) {
  const parts = String(name || '')
    .split(/\s+/)
    .map((part) => part.replace(/[^A-Za-z]/g, ''))
    .filter((part) => part && !/^(of|the|de|da|van|von)$/i.test(part));
  if (!parts.length) return '';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

function shortPlace(place) {
  if (!place) return '';
  const parts = place.split(',').map((part) => part.trim()).filter((part) => part && !/^of$/i.test(part));
  if (!parts.length) return '';
  const country = /^(united states|united kingdom|usa|u\.s\.a\.|us|england|wales|switzerland|schweiz|denmark|ireland|germany|british colonial america)$/i;
  const city = parts[0];
  const region = [...parts].reverse().find((part) => part !== city && !country.test(part));
  const regionShort = region ? region.replace(/\s+Territory$/i, '') : '';
  if (regionShort && regionShort.toLowerCase() !== city.toLowerCase()) return `${city}, ${regionShort}`;
  return city;
}

function lifeLine(person) {
  const years = person.birthYear && person.deathYear
    ? `${person.birthYear}–${person.deathYear}`
    : person.birthYear
      ? `b. ${person.birthYear}`
      : person.deathYear
        ? `d. ${person.deathYear}`
        : '';
  return [shortPlace(person.birthPlace), years].filter(Boolean).join('  ·  ').toUpperCase();
}

export function lineageHitTest(mx, my, positions, slop) {
  return hitTest(mx, my, positions, slop);
}

function easeForGen(gen, progress) {
  const delay = (gen - 1) * 0.032;
  const grow = Math.max(0, Math.min(1, (progress - delay) / 0.78));
  return grow * grow * (3 - 2 * grow);
}

const idealPool = new Map();
const displayPool = new Map();
const ringPool = new Map();

function pooledPoint(map, id) {
  let out = map.get(id);
  if (!out) {
    out = { x: 0, y: 0, r: 0, ringR: 0, angle: null, gen: 0, ease: 0 };
    map.set(id, out);
  }
  return out;
}

/** Ancestor positions growing out of the portrait as it moves. */
export function lineageDisplayPositions(layout, hx, hy, progress) {
  layout.positions.forEach((pos, id) => {
    const out = pooledPoint(idealPool, id);
    const person = BY_ID.get(id);
    out.ringR = pos.ringR || 0;
    out.angle = pos.angle ?? null;
    out.gen = pos.gen || 0;
    if (!person || person.gen === 0) {
      out.x = hx;
      out.y = hy;
      out.r = pos.r;
      out.ease = progress;
      return;
    }
    const ease = easeForGen(person.gen, progress);
    out.x = hx + (pos.x - layout.cx) * ease;
    out.y = hy + (pos.y - layout.cy) * ease;
    out.r = pos.r * (0.35 + 0.65 * ease);
    out.ease = ease;
  });
  return idealPool;
}

const followSpring = new Map();

/** Farther generations follow the pointer more loosely, so the fan trails instead of sliding as one piece. */
function lineageFollow(gen, id) {
  const cached = followSpring.get(id);
  if (cached != null) return cached;
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 33 + id.charCodeAt(i)) >>> 0;
  const wobble = ((hash % 100) / 100 - 0.5) * 0.008;
  const spring = Math.max(0.012, 0.05 - gen * 0.0055 + wobble);
  followSpring.set(id, spring);
  return spring;
}

function springOffset(body, targetX, targetY, spring) {
  const dx = targetX - body.x;
  const dy = targetY - body.y;
  if (dx * dx + dy * dy < 0.04 && body.vx * body.vx + body.vy * body.vy < 0.04) {
    body.x = targetX;
    body.y = targetY;
    body.vx = 0;
    body.vy = 0;
    return false;
  }
  const damping = 0.94;
  body.vx = (body.vx + dx * spring) * damping;
  body.vy = (body.vy + dy * spring) * damping;
  body.x += body.vx;
  body.y += body.vy;
  body.vx *= 0.84;
  body.vy *= 0.84;
  return true;
}

export function stepLineageMotion(ideal, parallaxX, parallaxY, motion) {
  let moving = false;
  let maxRing = 1;
  ideal.forEach((pos, id) => {
    const gen = pos.gen || 0;
    if (gen > maxRing) maxRing = gen;
    let body = motion.get(id);
    if (!body) {
      body = { x: parallaxX, y: parallaxY, vx: 0, vy: 0 };
      motion.set(id, body);
    }
    if (gen === 0) {
      body.x = parallaxX;
      body.y = parallaxY;
      body.vx = 0;
      body.vy = 0;
    } else if (springOffset(body, parallaxX, parallaxY, lineageFollow(gen, id))) {
      moving = true;
    }
    const out = pooledPoint(displayPool, id);
    out.x = pos.x + body.x;
    out.y = pos.y + body.y;
    out.r = pos.r;
    out.ringR = pos.ringR;
    out.angle = pos.angle;
    out.gen = gen;
    out.ease = pos.ease;
  });

  const self = ideal.get('I1');
  for (let gen = 1; gen <= maxRing; gen += 1) {
    const key = `ring:${gen}`;
    let body = motion.get(key);
    if (!body) {
      body = { x: parallaxX, y: parallaxY, vx: 0, vy: 0 };
      motion.set(key, body);
    }
    if (springOffset(body, parallaxX, parallaxY, Math.max(0.014, 0.05 - gen * 0.0055))) {
      moving = true;
    }
    let center = ringPool.get(gen);
    if (!center) {
      center = { x: 0, y: 0 };
      ringPool.set(gen, center);
    }
    center.x = (self?.x ?? 0) + body.x;
    center.y = (self?.y ?? 0) + body.y;
  }
  return { display: displayPool, ringCenters: ringPool, moving };
}

const ringOffsetPool = [];

/** Each ring eases toward the pointer by a different amount, so the rings slip apart instead of moving as one piece. */
export function stepRingMotion(parallaxX, parallaxY, maxGen, motion) {
  let moving = false;
  const offsets = ringOffsetPool;
  let hub = offsets[0];
  if (!hub) {
    hub = { x: 0, y: 0 };
    offsets[0] = hub;
  }
  hub.x = parallaxX;
  hub.y = parallaxY;
  const span = Math.max(1, maxGen - 1);
  for (let gen = 1; gen <= maxGen; gen += 1) {
    const key = `ringShift:${gen}`;
    let body = motion.get(key);
    if (!body) {
      body = { x: parallaxX, y: parallaxY, vx: 0, vy: 0 };
      motion.set(key, body);
    }
    const t = (gen - 1) / span;
    const follow = 1 - t * 0.85;
    const spring = 0.06 - t * 0.045;
    if (springOffset(body, parallaxX * follow, parallaxY * follow, spring)) moving = true;
    let off = offsets[gen];
    if (!off) {
      off = { x: 0, y: 0 };
      offsets[gen] = off;
    }
    off.x = body.x;
    off.y = body.y;
  }
  return { offsets, moving };
}

/** Place each settled ring at that ring's offset. */
export function shiftLineage(ideal, offsets, maxGen) {
  ideal.forEach((pos, id) => {
    const gen = pos.gen || 0;
    const off = offsets[gen] || offsets[0] || { x: 0, y: 0 };
    const out = pooledPoint(displayPool, id);
    out.x = pos.x + off.x;
    out.y = pos.y + off.y;
    out.r = pos.r;
    out.ringR = pos.ringR;
    out.angle = pos.angle;
    out.gen = gen;
    out.ease = pos.ease;
  });
  const self = ideal.get('I1');
  const gens = Math.max(1, maxGen || 1);
  for (let gen = 1; gen <= gens; gen += 1) {
    const off = offsets[gen] || offsets[0] || { x: 0, y: 0 };
    let center = ringPool.get(gen);
    if (!center) {
      center = { x: 0, y: 0 };
      ringPool.set(gen, center);
    }
    center.x = (self?.x ?? 0) + off.x;
    center.y = (self?.y ?? 0) + off.y;
  }
  return { display: displayPool, ringCenters: ringPool, ideal };
}

const spokeSegs = new Map();
const dotSegs = new Map();
const dotRectSegs = new Map();
const DOT_RADIUS = 3.2;

function segList(map, key) {
  let list = map.get(key);
  if (!list) {
    list = [];
    map.set(key, list);
  }
  return list;
}

function clearSegMap(map) {
  map.forEach((list) => {
    list.length = 0;
  });
}

function strokeSegs(ctx, map, styleFor) {
  const keys = [];
  map.forEach((list, key) => {
    if (list.length) keys.push(key);
  });
  keys.sort();
  keys.forEach((key) => {
    const list = map.get(key);
    const style = styleFor(key);
    ctx.beginPath();
    for (let i = 0; i < list.length; i += 4) {
      ctx.moveTo(list[i], list[i + 1]);
      ctx.lineTo(list[i + 2], list[i + 3]);
    }
    ctx.strokeStyle = style.stroke;
    ctx.globalAlpha = style.alpha;
    ctx.lineWidth = style.width;
    ctx.stroke();
  });
  ctx.globalAlpha = 1;
}

export function drawLineageSpokes(ctx, display, lit, hotOnly = false) {
  if (hotOnly && !lit) return;
  clearSegMap(spokeSegs);
  const opening = (display.get('I1')?.ease ?? 0) < 0.999;
  LINKED.forEach((person) => {
    const from = display.get(person.id);
    const to = display.get(person.childId);
    if (!from || !to || from.ease < 0.04) return;
    const hot = lit && lit.has(person.id);
    if (hotOnly && !hot) return;
    const color = hot ? '#9bebb9' : placeColor(person);
    const ease = opening ? from.ease : 1;
    const key = `${hot ? 1 : 0}|${color}|${ease}`;
    const list = segList(spokeSegs, key);
    list.push(from.x, from.y, to.x, to.y);
  });
  strokeSegs(ctx, spokeSegs, (key) => {
    const hot = key.charCodeAt(0) === 49;
    const split = key.indexOf('|');
    const rest = key.slice(split + 1);
    const colorSplit = rest.lastIndexOf('|');
    const color = rest.slice(0, colorSplit);
    const ease = Number(rest.slice(colorSplit + 1));
    return {
      stroke: hot ? 'rgba(155, 235, 185, 0.55)' : withAlpha(color, 0.34),
      alpha: ease,
      width: hot ? 1.35 : 1,
    };
  });
}

function drawContinuations(ctx, layout, display, angle0 = null, angle1 = null) {
  const { minR, maxR, maxGen, yScale = 1 } = layout;
  if (maxGen < 2) return;
  const people = BY_GEN.get(maxGen);
  if (!people) return;
  const lastGap = generationRingR(maxGen, minR, maxR, maxGen) - generationRingR(maxGen - 1, minR, maxR, maxGen);
  const stub = lastGap;
  people.forEach((person) => {
    const pos = display.get(person.id);
    if (!pos || pos.ease < 0.12 || pos.angle == null) return;
    if (angle0 != null && (pos.angle < angle0 || pos.angle >= angle1)) return;
    const reach = stub * pos.ease;
    if (reach <= pos.r + 1) return;
    const c = Math.cos(pos.angle);
    const s = Math.sin(pos.angle);
    const x1 = pos.x + c * pos.r;
    const y1 = pos.y + s * pos.r * yScale;
    const x2 = pos.x + c * reach;
    const y2 = pos.y + s * reach * yScale;
    const color = placeColor(person);
    const grad = ctx.createLinearGradient(x1, y1, x2, y2);
    grad.addColorStop(0, withAlpha(color, 0.5));
    grad.addColorStop(0.42, withAlpha(color, 0.16));
    grad.addColorStop(1, withAlpha(color, 0));
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = grad;
    ctx.globalAlpha = pos.ease;
    ctx.lineWidth = 1;
    ctx.stroke();
  });
  ctx.globalAlpha = 1;
}

function paintDotLists(ctx, map, mode) {
  map.forEach((list, key) => {
    if (!list.length) return;
    const split = key.lastIndexOf('|');
    const color = key.slice(0, split);
    const ease = Number(key.slice(split + 1));
    ctx.globalAlpha = ease;
    ctx.beginPath();
    if (mode === 'rect') {
      ctx.fillStyle = withAlpha(color, 0.92);
      for (let i = 0; i < list.length; i += 3) {
        const x = list[i];
        const y = list[i + 1];
        const d = list[i + 2] * 2 + 1.15;
        ctx.rect(x - d / 2, y - d / 2, d, d);
      }
      ctx.fill();
    } else {
      for (let i = 0; i < list.length; i += 3) {
        const x = list[i];
        const y = list[i + 1];
        const r = list[i + 2];
        ctx.moveTo(x + r, y);
        ctx.arc(x, y, r, 0, Math.PI * 2);
      }
      ctx.fillStyle = 'rgba(58, 60, 59, 0.96)';
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = withAlpha(color, 0.9);
      ctx.stroke();
    }
  });
  ctx.globalAlpha = 1;
}

function drawDots(ctx, display, hoverId, selectedId, narrow, onlyGen, angle0, angle1) {
  clearSegMap(dotSegs);
  clearSegMap(dotRectSegs);
  const opening = (display.get('I1')?.ease ?? 0) < 0.999;
  const labelGen = narrow ? 1 : 2;
  DRAW_ORDER.forEach((person) => {
    const pos = display.get(person.id);
    if (onlyGen && person.gen !== onlyGen) return;
    if (angle0 != null && (!pos || pos.angle == null || pos.angle < angle0 || pos.angle >= angle1)) return;
    if (!pos || pos.ease < 0.04) return;
    if (person.id === hoverId || person.id === selectedId || person.gen <= labelGen || pos.r >= DOT_RADIUS) return;
    const ease = opening ? pos.ease : 1;
    const key = `${placeColor(person)}|${ease}`;
    const map = pos.r < 2.15 ? dotRectSegs : dotSegs;
    segList(map, key).push(pos.x, pos.y, pos.r);
  });
  paintDotLists(ctx, dotRectSegs, 'rect');
  paintDotLists(ctx, dotSegs, 'arc');
}

export function drawLineageGlow(ctx, layout, hx, hy, open, progress) {
  if (open <= 0.02) return;
  const { minR, maxR, maxGen, yScale = 1, arcStart, arcSweep } = layout;
  const glowR = maxR * Math.max(open, easeForGen(maxGen, progress ?? open));
  const glow = ctx.createRadialGradient(hx, hy, minR * 0.2, hx, hy, Math.max(glowR, 8));
  glow.addColorStop(0, 'rgba(210, 190, 120, 0.08)');
  glow.addColorStop(0.45, 'rgba(80, 150, 110, 0.04)');
  glow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.save();
  ctx.globalAlpha = open;
  ctx.fillStyle = glow;
  if (yScale === 1) {
    ctx.beginPath();
    ctx.moveTo(hx, hy);
    ctx.arc(hx, hy, Math.max(glowR, 8), arcStart, arcStart + arcSweep);
    ctx.closePath();
  } else {
    traceFan(ctx, hx, hy, Math.max(glowR, 8), yScale, arcStart, arcSweep, true);
  }
  ctx.fill();
  ctx.restore();
}

export function drawLineageArcs(ctx, layout, display, hx, hy, ringCenters, onlyGen = 0) {
  const { minR, maxR, yScale = 1, maxGen, arcStart, arcSweep } = layout;
  const from = onlyGen || 1;
  const to = onlyGen || maxGen;
  for (let gen = from; gen <= to; gen += 1) {
    const ease = easeForGen(gen, display.get('I1')?.ease ?? 0);
    if (ease < 0.03) continue;
    const ringR = generationRingR(gen, minR, maxR, maxGen) * ease;
    const origin = ringCenters?.get(gen) || { x: hx, y: hy };
    if (yScale === 1) {
      ctx.beginPath();
      ctx.arc(origin.x, origin.y, ringR, arcStart, arcStart + arcSweep);
    } else {
      traceFan(ctx, origin.x, origin.y, ringR, yScale, arcStart, arcSweep, false);
    }
    ctx.strokeStyle = gen === maxGen ? 'rgba(212, 196, 120, 0.22)' : 'rgba(140, 190, 160, 0.14)';
    ctx.globalAlpha = ease;
    ctx.lineWidth = gen === maxGen ? 1.25 : 1;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
}

const SLICE_BINS = 8;

/** Small patches along each ring so a moving frame blits the people without repainting them. */
export function lineageRingSlices(layout, display, hx, hy, gen) {
  const { minR, maxR, maxGen, yScale = 1, arcStart, arcSweep, narrow } = layout;
  const people = BY_GEN.get(gen) || [];
  const labelPad = gen <= (narrow ? 1 : 2) ? 72 : 8;
  const slices = [];
  for (let i = 0; i < SLICE_BINS; i += 1) {
    const a0 = arcStart + (arcSweep * i) / SLICE_BINS;
    const a1 = arcStart + (arcSweep * (i + 1)) / SLICE_BINS + (i === SLICE_BINS - 1 ? 0.002 : 0);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let count = 0;
    const add = (x, y, pad) => {
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      minX = Math.min(minX, x - pad);
      minY = Math.min(minY, y - pad);
      maxX = Math.max(maxX, x + pad);
      maxY = Math.max(maxY, y + pad);
    };
    people.forEach((person) => {
      const pos = display.get(person.id);
      if (!pos || pos.angle == null || pos.angle < a0 || pos.angle >= a1) return;
      count += 1;
      add(pos.x, pos.y, pos.r + labelPad);
      if (gen === maxGen) {
        const gap = generationRingR(maxGen, minR, maxR, maxGen) - generationRingR(Math.max(1, maxGen - 1), minR, maxR, maxGen);
        const reach = gap * pos.ease;
        add(pos.x + Math.cos(pos.angle) * reach, pos.y + Math.sin(pos.angle) * reach * yScale, 4);
      }
    });
    if (!count || !Number.isFinite(minX)) continue;
    const x = Math.floor(minX) - 2;
    const y = Math.floor(minY) - 2;
    slices.push({
      x,
      y,
      w: Math.ceil(maxX) - x + 3,
      h: Math.ceil(maxY) - y + 3,
      a0,
      a1,
      title: false,
    });
  }
  if (gen === maxGen) {
    const ease = easeForGen(gen, display.get('I1')?.ease ?? 1);
    const size = narrow ? 11 : 13;
    const titleY = hy - maxR * ease * yScale - size * 0.9 - 22;
    const half = size * 9.5;
    slices.push({
      x: Math.floor(hx - half),
      y: Math.floor(titleY - size * 1.6),
      w: Math.ceil(half * 2),
      h: Math.ceil(size * 2.4),
      a0: null,
      a1: null,
      title: true,
    });
  }
  return slices;
}

export function drawLineage(ctx, layout, display, hx, hy, options = {}) {
  const {
    hoverId,
    selectedId,
    ringCenters,
    overlay = false,
    onlyGen = 0,
    glow = true,
    spokes = true,
    people = true,
    arcs = true,
    angle0 = null,
    angle1 = null,
    litIds = null,
  } = options;
  const { maxR, yScale = 1, maxGen } = layout;
  const focusId = hoverId || (selectedId && selectedId !== 'I1' ? selectedId : null);
  const lit = litIds || (focusId ? chainToRoot(focusId) : null);
  const open = easeForGen(1, Math.min(1, (display.get('I1')?.ease ?? 0) * 1.2));
  const drawTails = options.continuations !== undefined ? options.continuations : !onlyGen || onlyGen === maxGen;
  const drawTitle = options.title !== undefined ? options.title : !onlyGen || onlyGen === maxGen;

  if (glow && !overlay) drawLineageGlow(ctx, layout, hx, hy, open, display.get('I1')?.ease ?? 0);

  if (!overlay && arcs) drawLineageArcs(ctx, layout, display, hx, hy, ringCenters, onlyGen);

  if (overlay) {
    if (spokes) drawLineageSpokes(ctx, display, lit, true);
  } else {
    if (spokes) drawLineageSpokes(ctx, display, lit);
    if (drawTails) drawContinuations(ctx, layout, display, angle0, angle1);
    if (people) drawDots(ctx, display, hoverId, selectedId, layout.narrow, onlyGen, angle0, angle1);
  }

  if (people || overlay) {
  const labelGen = layout.narrow ? 1 : 2;
  DRAW_ORDER.forEach((person) => {
    const pos = display.get(person.id);
    if (onlyGen && person.gen !== onlyGen) return;
    if (angle0 != null && (!pos || pos.angle == null || pos.angle < angle0 || pos.angle >= angle1)) return;
    if (!pos || pos.ease < 0.04) return;
    const active = person.id === selectedId;
    const hover = person.id === hoverId;
    const placeMatch = !!(litIds && litIds.has(person.id));
    if (overlay && !hover && !active && !placeMatch) return;
    if (!hover && !active && !placeMatch && person.gen > labelGen && pos.r < DOT_RADIUS) return;
    if (overlay && pos.r < DOT_RADIUS) {
      const wipe = pos.r * 2 + 3.2;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000';
      ctx.fillRect(pos.x - wipe / 2, pos.y - wipe / 2, wipe, wipe);
      ctx.restore();
    }
    const color = placeColor(person);
    const radius = pos.r * (hover || placeMatch ? 1.08 : 1);
    const photo = radius >= DOT_RADIUS ? ancestorPhoto(person.photo) : null;
    const sprite = photo ? tintedSprite(photo, person.living) : null;
    ctx.globalAlpha = pos.ease;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
    if (sprite) {
      ctx.drawImage(sprite, pos.x - radius, pos.y - radius, radius * 2, radius * 2);
    } else {
      ctx.fillStyle = 'rgba(58, 60, 59, 0.96)';
      ctx.fill();
    }
    if (!sprite && radius >= 5) {
      const initials = initialsOf(person.name);
      if (initials) {
        const size = Math.max(6, Math.min(radius * 0.78, 13));
        ctx.font = `600 ${size}px KMM, Orbitron, sans-serif`;
        ctx.fillStyle = 'rgba(132, 136, 130, 0.92)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.letterSpacing = '0px';
        ctx.fillText(initials, pos.x, pos.y + size * 0.04);
      }
    }
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
    ctx.lineWidth = active || placeMatch ? 2.2 : 1.5;
    ctx.strokeStyle = active || hover || placeMatch ? color : withAlpha(color, 0.9);
    ctx.stroke();
    ctx.globalAlpha = 1;

    const showLabel = (hover || active || person.gen <= (layout.narrow ? 1 : 2)) && pos.ease > 0.55;
    if (!showLabel) return;
    const emphasized = hover || active;
    const name = (emphasized ? person.name : surnameOf(person)).toUpperCase();
    const meta = emphasized ? lifeLine(person) : '';
    const size = emphasized ? (layout.narrow ? 9 : 11) : 8;
    const dx = pos.x - hx;
    const dy = pos.y - hy;
    const len = Math.hypot(dx, dy) || 1;
    const outward = pos.r + size * (meta ? 1.15 : 0.85);
    const lx = pos.x + (dx / len) * outward;
    const ly = pos.y + (dy / len) * outward;
    let rot = Math.atan2(dy, dx) + Math.PI / 2;
    if (rot > Math.PI) rot -= Math.PI * 2;
    if (rot < -Math.PI) rot += Math.PI * 2;
    if (rot > Math.PI / 2) rot -= Math.PI;
    if (rot < -Math.PI / 2) rot += Math.PI;
    ctx.save();
    ctx.translate(lx, ly);
    ctx.rotate(rot);
    ctx.globalAlpha = pos.ease;
    ctx.font = `500 ${size}px KMM, Orbitron, sans-serif`;
    ctx.fillStyle = 'rgba(244, 239, 224, 0.92)';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.letterSpacing = `${size * 0.08}px`;
    ctx.fillText(name, 0, meta ? -size * 0.62 : 0);
    if (meta) {
      const metaSize = Math.max(6, size - 3);
      ctx.font = `500 ${metaSize}px KMM, Orbitron, sans-serif`;
      ctx.fillStyle = withAlpha(color, 0.95);
      ctx.letterSpacing = `${metaSize * 0.06}px`;
      ctx.fillText(meta, 0, size * 0.62);
    }
    ctx.letterSpacing = '0px';
    ctx.restore();
  });
  }

  if (overlay || !drawTitle) return;

  const outerEase = easeForGen(maxGen, display.get('I1')?.ease ?? 0);
  if (outerEase > 0.35) {
    const origin = ringCenters?.get(maxGen) || { x: hx, y: hy };
    const outerR = maxR * outerEase;
    const size = layout.narrow ? 11 : 13;
    ctx.save();
    ctx.globalAlpha = Math.min(1, (outerEase - 0.35) / 0.35);
    ctx.font = `700 ${size}px KMM, Orbitron, sans-serif`;
    ctx.fillStyle = 'rgba(255, 248, 230, 0.72)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.letterSpacing = `${size * 0.22}px`;
      ctx.fillText('MILLS FAMILY TREE', origin.x, origin.y - outerR * yScale - size * 0.9 - 22);
    ctx.letterSpacing = '0px';
    ctx.restore();
  }
}
